import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, CalendarIcon } from '@heroicons/react/24/outline';

interface MonthYearPickerProps {
  value: { year: number; month: number };
  onChange: (value: { year: number; month: number }) => void;
  timezone?: string;
  className?: string;
}

// Helper function to convert UTC timestamp to gateway timezone
const convertToGatewayTimezone = (utcTimestamp: string, timezone?: string): Date => {
  if (!timezone) {
    return new Date(utcTimestamp);
  }
  
  const utcDate = new Date(utcTimestamp);
  
  // Use Intl.DateTimeFormat to convert to gateway timezone
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false
  });
  
  const parts = formatter.formatToParts(utcDate);
  
  // Extract values from parts
  const year = parseInt(parts.find(part => part.type === 'year')?.value || '0');
  const month = parseInt(parts.find(part => part.type === 'month')?.value || '0') - 1; // Month is 0-based
  const day = parseInt(parts.find(part => part.type === 'day')?.value || '0');
  const hour = parseInt(parts.find(part => part.type === 'hour')?.value || '0');
  const minute = parseInt(parts.find(part => part.type === 'minute')?.value || '0');
  const second = parseInt(parts.find(part => part.type === 'second')?.value || '0');
  
  return new Date(year, month, day, hour, minute, second);
};

export const MonthYearPicker: React.FC<MonthYearPickerProps> = ({
  value,
  onChange,
  timezone,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewYear, setViewYear] = useState(value.year);
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
  const [dropdownAlign, setDropdownAlign] = useState<'left' | 'right'>('left');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Get current date in the specified timezone
  const getCurrentDate = () => {
    const now = new Date();
    if (timezone) {
      const nowInTimezone = convertToGatewayTimezone(now.toISOString(), timezone);
      return {
        year: nowInTimezone.getFullYear(),
        month: nowInTimezone.getMonth()
      };
    } else {
      return {
        year: now.getFullYear(),
        month: now.getMonth()
      };
    }
  };

  const currentDate = getCurrentDate();

  // Calculate optimal dropdown position
  const calculateDropdownPosition = () => {
    if (!triggerRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const dropdownHeight = 400; // Approximate dropdown height
    const dropdownWidth = 280; // Dropdown width
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // Check vertical space
    const spaceBelow = viewportHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    
    // Choose vertical position
    const shouldShowAbove = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
    setDropdownPosition(shouldShowAbove ? 'top' : 'bottom');

    // Check horizontal space
    const spaceRight = viewportWidth - triggerRect.left;
    const spaceLeft = triggerRect.right;
    
    // Choose horizontal alignment
    const shouldAlignRight = spaceRight < dropdownWidth && spaceLeft > spaceRight;
    setDropdownAlign(shouldAlignRight ? 'right' : 'left');
  };

  // Update position when opening
  useEffect(() => {
    if (isOpen) {
      calculateDropdownPosition();
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleResize = () => {
      if (isOpen) {
        calculateDropdownPosition();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
    };
  }, [isOpen]);

  // Month names
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Check if a month/year combination is in the future
  const isFutureMonth = (year: number, month: number) => {
    if (year > currentDate.year) return true;
    if (year === currentDate.year && month > currentDate.month) return true;
    return false;
  };

  const handleMonthClick = (month: number) => {
    if (!isFutureMonth(viewYear, month)) {
      onChange({ year: viewYear, month });
      setIsOpen(false);
    }
  };

  const formatDisplayValue = () => {
    return `${monthNames[value.month]} ${value.year}`;
  };

  const handlePreviousYear = () => {
    const newYear = viewYear - 1;
    if (newYear >= 2024) {
      setViewYear(newYear);
    }
  };

  const handleNextYear = () => {
    const newYear = viewYear + 1;
    if (newYear <= currentDate.year) {
      setViewYear(newYear);
    }
  };

  // Calculate dropdown styles based on position
  const getDropdownStyles = () => {
    const styles: React.CSSProperties = {
      position: 'absolute',
      zIndex: 50,
      minWidth: '280px'
    };

    if (dropdownPosition === 'top') {
      styles.bottom = '100%';
      styles.marginBottom = '4px';
    } else {
      styles.top = '100%';
      styles.marginTop = '4px';
    }

    if (dropdownAlign === 'right') {
      styles.right = '0';
    } else {
      styles.left = '0';
    }

    return styles;
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger button */}
      <button
        ref={triggerRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 bg-gray-100 dark:bg-[#292929] border border-gray-200 dark:border-[#404040] rounded-md px-3 py-2 text-gray-800 dark:text-white text-sm hover:bg-gray-200 dark:hover:bg-[#353535] focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors"
      >
        <CalendarIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
        <span>{formatDisplayValue()}</span>
        <ChevronLeftIcon 
          className={`w-4 h-4 text-gray-500 dark:text-gray-400 transform transition-transform ${isOpen ? 'rotate-90' : '-rotate-90'}`} 
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div 
          style={getDropdownStyles()}
          className="bg-white dark:bg-[#1D1D1D] border border-gray-200 dark:border-[#404040] rounded-md shadow-lg"
        >
          {/* Year navigation */}
          <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-[#404040]">
            <button
              onClick={handlePreviousYear}
              disabled={viewYear <= 2024}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-[#353535] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeftIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            </button>
            
            <span className="text-gray-800 dark:text-white font-medium">{viewYear}</span>
            
            <button
              onClick={handleNextYear}
              disabled={viewYear >= currentDate.year}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-[#353535] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRightIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            </button>
          </div>

          {/* Month grid */}
          <div className="grid grid-cols-3 gap-1 p-3">
            {monthNames.map((monthName, monthIndex) => {
              const isFuture = isFutureMonth(viewYear, monthIndex);
              const isSelected = value.year === viewYear && value.month === monthIndex;
              const isCurrent = currentDate.year === viewYear && currentDate.month === monthIndex;

              return (
                <button
                  key={monthIndex}
                  onClick={() => handleMonthClick(monthIndex)}
                  disabled={isFuture}
                  className={`
                    px-3 py-2 text-sm rounded transition-colors relative
                    ${isSelected 
                      ? 'bg-blue-600 text-white' 
                      : isFuture 
                        ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed' 
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#353535] hover:text-gray-900 dark:hover:text-white'
                    }
                    ${isCurrent && !isSelected ? 'ring-1 ring-blue-400' : ''}
                  `}
                >
                  {monthName.slice(0, 3)}
                  {isCurrent && (
                    <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-blue-400 rounded-full"></div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Today button */}
          <div className="border-t border-gray-200 dark:border-[#404040] p-3">
            <button
              onClick={() => {
                onChange({ year: currentDate.year, month: currentDate.month });
                setIsOpen(false);
              }}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors"
            >
              Go to Current Month
            </button>
          </div>
        </div>
      )}
    </div>
  );
}; 